import React from "react";
import ServicePageLayout, { H2, H3, P, UL } from "./ServicePageLayout";
import Certifications from "@/components/Certifications";

export const meta = {
  title: "Southern Alberta Networking & Wireless | Rural + Commercial | Epicom Systems",
  metaDescription:
    "Networking, wireless internet, cellular DAS and communication tower work across Southern Alberta. Based in the Lethbridge corridor, working the band from the Cardston foothills east through Taber and out to Medicine Hat.",
  canonicalPath: "/southern-alberta",
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://epicom.ca/southern-alberta#service",
    "name": "Southern Alberta Networking & Wireless Contracting",
    "serviceType": "Regional networking, wireless, cellular and communication infrastructure",
    "provider": { "@type": "LocalBusiness", "@id": "https://epicom.ca/#business" },
    "areaServed": { "@type": "AdministrativeArea", "name": "Southern Alberta" },
    "description":
      "Regional page covering Epicom Systems' networking, wireless, cellular and tower work across Southern Alberta — Lethbridge corridor east through Taber and Medicine Hat, west into the Cardston/Pincher Creek foothills, and the communities in between.",
    "url": "https://epicom.ca/southern-alberta",
  },
};

export default function SouthernAlberta() {
  return (
    <ServicePageLayout
      title={meta.title}
      metaDescription={meta.metaDescription}
      canonicalPath={meta.canonicalPath}
      overline="Southern Alberta"
      h1="Networking & Wireless Contracting Across Southern Alberta"
      intro="Epicom Systems is based in the Lethbridge corridor and works across Southern Alberta — from the Cardston foothills east through Taber and out to Medicine Hat, and north through Fort Macleod and Claresholm. This page is the regional hub: what we do, where we do it, and how a rural-and-commercial region actually shapes the way we design the work."
      jsonLd={meta.jsonLd}
      relatedLinks={[
        { to: "/lethbridge", label: "Lethbridge Services" },
        { to: "/cellular-das", label: "Cellular DAS" },
        { to: "/wireless-infrastructure", label: "Wireless Infrastructure" },
      ]}
    >
      <H2>What we do across the region</H2>
      <UL>
        <><a href="/wireless-infrastructure" className="text-signal hover:underline"><strong className="text-white/90">Wireless infrastructure</strong></a> — point-to-point and point-to-multipoint links, multi-building networks, backhaul, SCADA and remote-endpoint connectivity.</>
        <><a href="/unifi-installation" className="text-signal hover:underline"><strong className="text-white/90">UniFi networking</strong></a> — full Ubiquiti UniFi environments for businesses, acreages and farm operations, tied together as one manageable system.</>
        <><a href="/business-wifi" className="text-signal hover:underline"><strong className="text-white/90">Commercial Wi-Fi</strong></a> — engineered Wi-Fi coverage for offices, retail, industrial and multi-building sites, planned around real floor plans rather than guesswork.</>
        <><a href="/cellular-das" className="text-signal hover:underline"><strong className="text-white/90">Cellular DAS</strong></a> — commercial in-building cellular coverage where the building itself is the reason nobody has bars.</>
        <><a href="/cell-boosters" className="text-signal hover:underline"><strong className="text-white/90">Cell boosters</strong></a> — properly-installed cellular signal boosters for homes, acreages and small commercial spaces.</>
        <><a href="/security-cameras" className="text-signal hover:underline"><strong className="text-white/90">Security cameras</strong></a> and <a href="/access-control" className="text-signal hover:underline"><strong className="text-white/90">access control</strong></a> — commercial-grade cameras and electronic entry systems integrated with the same UniFi network as the rest of the site.</>
        <><a href="/towers" className="text-signal hover:underline"><strong className="text-white/90">Communication towers</strong></a> — small and medium tower builds plus custom communications infrastructure using existing structures.</>
      </UL>

      <H2>Where we work</H2>
      <P>
        Home-base is the Lethbridge corridor. From there we routinely work east through
        Coaldale, Coalhurst, Taber, Barnwell, Burdett, Bow Island, Foremost and out to the
        Medicine Hat area; west and southwest through Magrath, Raymond, Cardston, Glenwood,
        Hillspring, Waterton, Pincher Creek, Fort Macleod and Claresholm; and north through
        Vulcan, Granum and communities throughout Southern Alberta. We also travel for
        larger commercial and rural projects beyond the immediate corridor when the project
        warrants the trip.
      </P>
      <P>
        The point of listing towns isn’t to game a search engine — it’s to be honest about
        where we already work regularly. If your operation is somewhere in this general
        region and involves the services above, chances are good we’ve done a similar job
        nearby.
      </P>

      <H2>What makes Southern Alberta a specific place to build networks</H2>
      <H3>Distance is the default</H3>
      <P>
        A lot of the work here is not urban. Farms, acreages, feedlots, rural businesses,
        industrial sites out on the flats, tourism and commercial operators tucked into
        the foothills. Distance and terrain drive design decisions — where a tower or
        <a href="/towers" className="text-signal hover:underline"> creative structure</a> goes,
        where <a href="/point-to-point-wireless" className="text-signal hover:underline">wireless links</a> land,
        and how you tie a remote building back to a main site without trenching for
        kilometres.
      </P>
      <H3>Weather is a real constraint</H3>
      <P>
        Wind on the ridges, ice and snow across the winter, chinook cycles that expand and
        contract everything, blowing dust in the summer. Hardware and mounting have to
        survive that cycle for years, not just look good on installation day.
      </P>
      <H3>Buildings are hard on cellular</H3>
      <P>
        Steel-clad shops, ICF construction, concrete tilt-up commercial buildings, low-E
        windows — all attenuate cellular signal, which is why{" "}
        <a href="/cellular-das" className="text-signal hover:underline">DAS</a> and{" "}
        <a href="/cell-boosters" className="text-signal hover:underline">boosters</a> are a
        common part of what we install, especially further from the nearest carrier tower.
        For the equipment side we hold current manufacturer training as a{" "}
        <strong className="text-white/90">SureCall Certified Installer</strong>,{" "}
        <strong className="text-white/90">WilsonPro Certified Installer / Integrator</strong>,
        and <strong className="text-white/90">Nextivity Certified Partner</strong> (with
        CEL-FI Enterprise Sales & Installation certification) for active in-building
        cellular systems.
      </P>
      <H3>Commercial and rural often overlap</H3>
      <P>
        A lot of Southern Alberta business is agriculture-adjacent: shops and offices tied
        to a farm operation, food and grain processing, feedlots, service businesses that
        run out of the same yard the family does. The network design has to reflect that
        overlap — “house Wi-Fi” and “shop Wi-Fi” and “office Wi-Fi” often need to be one
        environment, done properly, with sensible segmentation between them.
      </P>

      <H2>Focused city page</H2>
      <P>
        For customers in and around Lethbridge specifically, see our dedicated{" "}
        <a href="/lethbridge" className="text-signal hover:underline">Lethbridge services</a> page — it
        covers the services most relevant to commercial customers in the city and the
        immediate surrounding area.
      </P>

      <H2>Working with Epicom regionally</H2>
      <P>
        Whether the site is 10 minutes from Lethbridge or an hour into the foothills, the
        approach is the same: real site walk, honest scope, engineered design, and an
        install that will still be doing its job several winters from now. Get in touch to
        start a conversation about what your site needs.
      </P>

      <Certifications variant="compact" className="!py-8 md:!py-10" />
    </ServicePageLayout>
  );
}
